// Draws a NameIt! results card as a 1080 x 1350 (4:5) image in the main game's look:
// orange-50 ground, one white panel, red accents and bold sans-serif type. One mascot
// peeks over the panel's top edge, the way they peek over answer cards in the game.
import { SITE_URL, gameFacts, pickRows } from './resultCards';

const W = 1080;
const H = 1350;
const PAD = 60;
const PANEL_TOP = 300;
const PANEL_BOTTOM = H - 196;
const INNER = PAD + 56;

const BG = '#fff7ed';
const RED = '#dc2626';
const RED_DARK = '#991b1b';
const RED_TINT = '#fef2f2';
const TEXT = '#1f2937';
const MUTED = '#6b7280';
const LIGHT = '#9ca3af';
const LINE = '#e5e7eb';
const CHIP = '#ffedd5';
const PLACES = [
  { fill: '#fef3c7', edge: '#facc15', dot: '#eab308' },
  { fill: '#f3f4f6', edge: '#d1d5db', dot: '#9ca3af' },
  { fill: '#ffedd5', edge: '#fdba74', dot: '#f97316' },
];

const SANS = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const font = (weight, size) => `${weight} ${size}px ${SANS}`;

// Draws with system fonts, so there is nothing to fetch; this only lets pending fonts settle.
export async function cardFontsReady() {
  if (typeof document === 'undefined' || !document.fonts?.ready) return;
  await Promise.race([document.fonts.ready.catch(() => null), new Promise((r) => setTimeout(r, 1200))]);
}

function roundedRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function fit(g, text, weight, size, maxWidth, min = 30) {
  let px = size;
  g.font = font(weight, px);
  while (px > min && g.measureText(text).width > maxWidth) {
    px -= 2;
    g.font = font(weight, px);
  }
  return px;
}

function write(g, text, x, y, { weight = 600, size = 36, color = TEXT, align = 'left', max } = {}) {
  g.fillStyle = color;
  if (max) fit(g, text, weight, size, max);
  else g.font = font(weight, size);
  g.textAlign = align;
  g.fillText(text, x, y);
}

// Mascot art keeps its own proportions, standing on the bottom of its box.
function drawMascot(g, img, x, y, w, h) {
  if (!img) return;
  const scale = Math.min(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  g.drawImage(img, x + (w - dw) / 2, y + h - dh, dw, dh);
}

const ordinal = (n) => `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10 > 3 || [11, 12, 13].includes(n % 100) ? 0 : n % 10] || 'th'}`;

function header(g, kind, logo) {
  g.fillStyle = BG;
  g.fillRect(0, 0, W, H);
  g.textBaseline = 'alphabetic';
  let x = PAD;
  if (logo) {
    g.save();
    roundedRect(g, x, 52, 84, 84, 20);
    g.clip();
    g.drawImage(logo, x, 52, 84, 84);
    g.restore();
    x += 104;
  }
  write(g, 'NameIt!', x, 116, { weight: 800, size: 58, color: RED });
  write(g, kind, W - PAD, 112, { weight: 700, size: 32, color: MUTED, align: 'right' });
}

// The mascot is drawn before the panel, so the panel's top edge hides its lower half.
function panel(g, peeker) {
  // Left of the corner label, so the two never overlap.
  if (peeker) drawMascot(g, peeker, W - PAD - 470, PANEL_TOP - 236, 240, 300);
  g.save();
  g.shadowColor = 'rgba(31, 41, 55, 0.14)';
  g.shadowBlur = 40;
  g.shadowOffsetY = 14;
  roundedRect(g, PAD, PANEL_TOP, W - PAD * 2, PANEL_BOTTOM - PANEL_TOP, 48);
  g.fillStyle = '#fff';
  g.fill();
  g.restore();
}

function headline(g, text) {
  write(g, text, INNER, PANEL_TOP + 128, { weight: 800, size: 100, color: RED_DARK, max: W - INNER * 2 });
}

function footer(g, facts, letters, path = '') {
  const y = H - 128;
  write(g, facts, PAD, y, { weight: 600, size: 34, color: MUTED });
  g.font = font(600, 34);
  let x = PAD + g.measureText(facts).width;
  if (letters.length) {
    write(g, ' · letters', x, y, { weight: 600, size: 34, color: MUTED });
    x += g.measureText(' · letters').width + 16;
    const shown = letters.length > 8 ? letters.slice(0, 7) : letters;
    for (const letter of shown) {
      roundedRect(g, x, y - 36, 48, 48, 12);
      g.fillStyle = '#fff';
      g.fill();
      g.lineWidth = 3;
      g.strokeStyle = LINE;
      g.stroke();
      write(g, letter, x + 24, y, { weight: 800, size: 30, color: RED, align: 'center' });
      x += 56;
    }
    if (letters.length > shown.length) write(g, `+${letters.length - shown.length}`, x + 4, y, { weight: 700, size: 30, color: MUTED });
  }
  write(g, `${SITE_URL.replace(/^https?:\/\//, '')}${path}`, PAD, H - 60, { weight: 800, size: 36, color: RED });
}

function chip(g, x, y, answer, category, measureOnly = false) {
  g.font = font(800, 42);
  const aw = g.measureText(answer).width;
  g.font = font(700, 26);
  const w = aw + g.measureText(category.toUpperCase()).width + 70;
  if (measureOnly) return w;
  roundedRect(g, x, y, w, 70, 35);
  g.fillStyle = CHIP;
  g.fill();
  write(g, answer, x + 28, y + 49, { weight: 800, size: 42 });
  write(g, category.toUpperCase(), x + 28 + aw + 14, y + 46, { weight: 700, size: 26, color: MUTED });
  return w;
}

function standingsRows(g, list, top, bottom, mark, rowH = 82) {
  let y = top;
  for (const item of pickRows(list, Math.floor((bottom - top) / rowH), mark)) {
    if (item.type === 'more') {
      write(g, `+${item.n} more`, W / 2, y + rowH * 0.62, { weight: 600, size: 34, color: LIGHT, align: 'center' });
      y += rowH;
      continue;
    }
    const p = item.p;
    const mid = y + (rowH - 8) / 2;
    if (p.id === mark) {
      roundedRect(g, INNER - 24, y, W - INNER * 2 + 48, rowH - 8, 22);
      g.fillStyle = RED_TINT;
      g.fill();
    }
    const medal = PLACES[p.place - 1];
    if (medal) {
      g.beginPath();
      g.arc(INNER + 18, mid, 26, 0, Math.PI * 2);
      g.fillStyle = medal.dot;
      g.fill();
    }
    write(g, `${p.place}`, INNER + 18, mid + 12, { weight: 800, size: 32, color: medal ? '#fff' : LIGHT, align: 'center' });
    write(g, p.name, INNER + 70, mid + 14, { weight: 700, size: 40, max: 520 });
    write(g, `${p.total.toLocaleString('en-US')} pts`, W - INNER, mid + 14, { weight: 800, size: 40, color: RED, align: 'right' });
    g.fillStyle = LINE;
    g.fillRect(INNER, y + rowH - 4, W - INNER * 2, 2);
    y += rowH;
  }
}

// Answers only the winner thought of: equal tiles, category above, like the game's answer cards.
function onlyMine(g, answers, top) {
  write(g, 'Only I got these', INNER, top, { weight: 700, size: 34, color: MUTED });
  const gap = 16;
  const tileW = (W - INNER * 2 - gap * 2) / 3;
  const tileH = 100;
  answers.slice(0, 3).forEach((u, i) => {
    const x = INNER + i * (tileW + gap);
    const y = top + 24;
    roundedRect(g, x, y, tileW, tileH, 24);
    g.fillStyle = CHIP;
    g.fill();
    write(g, u.category.toUpperCase(), x + 24, y + 36, { weight: 700, size: 24, color: MUTED });
    write(g, u.answer, x + 24, y + 80, { weight: 800, size: 40, max: tileW - 48 });
  });
  return top + 24 + tileH;
}

function drawWon(g, card, copy, art) {
  panel(g, art[card.me.id]);
  headline(g, copy.headline);
  const score = card.me.total.toLocaleString('en-US');
  const ahead = card.margin > 0 ? `+${card.margin} ahead` : '';
  g.font = font(800, 40);
  const pillW = ahead ? g.measureText(ahead).width + 60 : 0;
  let size = 184;
  let sw;
  do {
    g.font = font(800, size);
    sw = g.measureText(score).width;
    size -= 6;
  } while (size > 110 && sw + 150 + pillW > W - INNER * 2);
  const base = PANEL_TOP + 336;
  write(g, score, INNER - 6, base, { weight: 800, size: size + 6, color: RED });
  write(g, 'pts', INNER + sw + 12, base - 6, { weight: 600, size: 52, color: MUTED });
  if (ahead) {
    const px = INNER + sw + 120;
    roundedRect(g, px, base - 76, pillW, 76, 38);
    g.fillStyle = RED_TINT;
    g.fill();
    write(g, ahead, px + 30, base - 24, { weight: 800, size: 40, color: RED });
  }

  let y = base + 40;
  if (card.unique.length) y = onlyMine(g, card.unique, base + 78) + 18;
  const others = [...card.podium, ...card.rest].filter((p) => p.id !== card.me.id);
  if (others.length) standingsRows(g, others, y, PANEL_BOTTOM - 20, card.me.id, 76);
}

function drawVotedOut(g, card, copy, art, pick) {
  const v = card.votedOut[pick % card.votedOut.length];
  const mine = v.id === card.me?.id;
  panel(g, art[v.id]);
  headline(g, copy.headline);

  write(g, `${v.category} starting with ${v.letter}`, INNER, PANEL_TOP + 236, { weight: 600, size: 40, color: MUTED });
  g.fillStyle = TEXT;
  const px = fit(g, v.answer, 800, 160, W - INNER * 2, 70);
  const base = PANEL_TOP + 236 + px * 1.08;
  g.textAlign = 'left';
  g.fillText(v.answer, INNER, base);
  const aw = g.measureText(v.answer).width;
  g.strokeStyle = RED;
  g.lineWidth = 14;
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(INNER - 12, base - px * 0.34);
  g.lineTo(INNER + aw + 12, base - px * 0.34);
  g.stroke();

  // The verdict, stamped across the bottom of the answer.
  g.save();
  g.translate(W - INNER - 210, base + 64);
  g.rotate((-7 * Math.PI) / 180);
  g.font = font(800, 60);
  const sw = g.measureText(copy.stamp).width + 64;
  roundedRect(g, -sw / 2, -52, sw, 104, 18);
  g.fillStyle = 'rgba(255, 255, 255, 0.9)';
  g.fill();
  g.lineWidth = 9;
  g.strokeStyle = RED;
  g.stroke();
  write(g, copy.stamp, 0, 21, { weight: 800, size: 60, color: RED, align: 'center' });
  g.restore();

  write(g, `${mine ? 'My' : `${v.name}'s`} answer · round ${v.round}`, INNER, base + 210, { weight: 600, size: 38, color: MUTED });

  // The sharer is always on their own card.
  const lines = [];
  if (card.winner) lines.push(`${card.winner.name} won`);
  if (card.me && card.me.id !== card.winner?.id) lines.push(`${card.me.name} came ${ordinal(card.me.place)}`);
  if (lines.length) write(g, lines.join(' · '), INNER, PANEL_BOTTOM - 64, { weight: 700, size: 40, max: W - INNER * 2 });
}

function drawPodium(g, card, copy, art) {
  panel(g, card.winner && art[card.winner.id]);
  headline(g, copy.headline);
  const h = 124;
  const restRows = Math.min(card.rest.length, 4);
  const block = card.podium.length * (h + 18) + (restRows ? 10 + restRows * 82 : 0);
  const space = PANEL_BOTTOM - 40 - (PANEL_TOP + 190);
  let y = PANEL_TOP + 190 + Math.max(0, (space - block) / 2);
  card.podium.forEach((p, i) => {
    const look = PLACES[i];
    roundedRect(g, INNER - 24, y, W - INNER * 2 + 48, h, 28);
    g.fillStyle = look.fill;
    g.fill();
    g.lineWidth = p.id === card.me?.id ? 6 : 3;
    g.strokeStyle = p.id === card.me?.id ? RED : look.edge;
    g.stroke();
    g.beginPath();
    g.arc(INNER + 30, y + h / 2, 34, 0, Math.PI * 2);
    g.fillStyle = look.dot;
    g.fill();
    write(g, `${i + 1}`, INNER + 30, y + h / 2 + 14, { weight: 800, size: 40, color: '#fff', align: 'center' });
    write(g, p.name, INNER + 90, y + h / 2 + 16, { weight: 800, size: 46, max: 500 });
    write(g, `${p.total.toLocaleString('en-US')} pts`, W - INNER, y + h / 2 + 16, { weight: 800, size: 44, color: RED, align: 'right' });
    y += h + 18;
  });
  if (card.rest.length) standingsRows(g, card.rest, y + 10, PANEL_BOTTOM - 24, card.me?.id);
}

// The universal room card: everyone in the game, best first, with the sharer picked out.
// The won, voted-out and podium cards above are kept but no longer offered.
const STEPS = {
  1: { top: '#fde68a', face: ['#fcd34d', '#f59e0b'], ink: '#78350f', label: '1ST', ratio: 1 },
  2: { top: '#f3f4f6', face: ['#e5e7eb', '#9ca3af'], ink: '#374151', label: '2ND', ratio: 0.8 },
  3: { top: '#fed7aa', face: ['#fdba74', '#ea580c'], ink: '#7c2d12', label: '3RD', ratio: 0.64 },
};
const YELLOW = '#fde047';

// Seeded so the same game always draws the same confetti.
function seeded(text) {
  let h = 2166136261;
  for (const ch of text) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

function backdrop(g, cx, cy, rng) {
  const bg = g.createRadialGradient(cx, cy, 60, cx, cy, 1100);
  bg.addColorStop(0, '#ef4444');
  bg.addColorStop(1, '#991b1b');
  g.fillStyle = bg;
  g.fillRect(0, 0, W, H);

  g.save();
  g.translate(cx, cy);
  g.fillStyle = 'rgba(255, 255, 255, 0.06)';
  const rays = 18;
  for (let i = 0; i < rays; i++) {
    const a = (i / rays) * Math.PI * 2;
    g.beginPath();
    g.moveTo(0, 0);
    g.arc(0, 0, 1500, a, a + Math.PI / rays);
    g.closePath();
    g.fill();
  }
  g.restore();

  const colors = [YELLOW, '#ffffff', '#fdba74', '#fca5a5'];
  for (let i = 0; i < 70; i++) {
    const x = rng() * W;
    const y = rng() * H * 0.62;
    // Keep the middle clear, where the title sits.
    if (x > 200 && x < W - 200 && y < 480) continue;
    g.save();
    g.translate(x, y);
    g.rotate(rng() * Math.PI);
    g.globalAlpha = 0.55 + rng() * 0.35;
    g.fillStyle = colors[i % colors.length];
    if (i % 3 === 0) {
      g.beginPath();
      g.arc(0, 0, 5 + rng() * 4, 0, Math.PI * 2);
      g.fill();
    } else {
      g.fillRect(-9, -4, 18, 8);
    }
    g.restore();
  }
}

function badge(g, text, cx, cy, { fill = YELLOW, color = '#7f1d1d', size = 26 } = {}) {
  g.font = font(800, size);
  const w = g.measureText(text).width + size * 1.3;
  const h = size * 1.7;
  g.save();
  g.shadowColor = 'rgba(0, 0, 0, 0.25)';
  g.shadowBlur = 10;
  g.shadowOffsetY = 3;
  roundedRect(g, cx - w / 2, cy - h / 2, w, h, h / 2);
  g.fillStyle = fill;
  g.fill();
  g.restore();
  write(g, text, cx, cy + size * 0.36, { weight: 800, size, color, align: 'center' });
}

function step(g, p, look, x, w, h, baseline, mascot, img, mine) {
  const top = baseline - h;
  const lip = 26;
  const cx = x + w / 2;

  // Lighter top face, set back a little, so each block reads as a step.
  g.beginPath();
  g.moveTo(x, top);
  g.lineTo(x + w, top);
  g.lineTo(x + w - 18, top - lip);
  g.lineTo(x + 18, top - lip);
  g.closePath();
  g.fillStyle = look.top;
  g.fill();

  const face = g.createLinearGradient(0, top, 0, baseline);
  face.addColorStop(0, look.face[0]);
  face.addColorStop(1, look.face[1]);
  g.fillStyle = face;
  g.fillRect(x, top, w, h);

  // A big faint place number fills the lower part of a tall step.
  if (h > 230) {
    g.save();
    g.globalAlpha = 0.16;
    write(g, `${look === STEPS[1] ? 1 : look === STEPS[2] ? 2 : 3}`, cx, baseline - 18, { weight: 800, size: Math.min(220, h - 170), color: look.ink, align: 'center' });
    g.restore();
  }

  if (mine) {
    g.lineWidth = 8;
    g.strokeStyle = '#fff';
    g.strokeRect(x + 4, top + 4, w - 8, h - 4);
  }

  g.save();
  if (typeof g.letterSpacing === 'string') g.letterSpacing = '4px';
  write(g, look.label, cx, top + 46, { weight: 800, size: 26, color: look.ink, align: 'center' });
  g.restore();
  write(g, p.name, cx, top + 100, { weight: 800, size: 44, color: look.ink, align: 'center', max: w - 36 });
  write(g, `${p.total.toLocaleString('en-US')} pts`, cx, top + 146, { weight: 700, size: 34, color: look.ink, align: 'center' });

  // The mascot stands on the top face, on a soft shadow.
  const feet = top - lip / 2;
  g.beginPath();
  g.ellipse(cx, feet, mascot * 0.32, 10, 0, 0, Math.PI * 2);
  g.fillStyle = 'rgba(0, 0, 0, 0.18)';
  g.fill();
  let headTop = feet;
  if (img) {
    const s = Math.min(mascot / img.width, mascot / img.height);
    headTop = feet + 4 - img.height * s;
    g.drawImage(img, cx - (img.width * s) / 2, headTop, img.width * s, img.height * s);
  }
  if (look === STEPS[1]) write(g, '👑', cx, headTop - 4, { size: Math.round(mascot * 0.3), align: 'center' });
  if (mine) badge(g, 'YOU', cx + w / 2 - 46, Math.max(headTop + 20, feet - mascot * 0.75), { size: 26 });
}

function standingsList(g, rows, meId, top, rowH) {
  const x = PAD;
  const w = W - PAD * 2;
  const h = rows.length * rowH + 28;
  g.save();
  g.shadowColor = 'rgba(0, 0, 0, 0.22)';
  g.shadowBlur = 30;
  g.shadowOffsetY = 10;
  roundedRect(g, x, top, w, h, 36);
  g.fillStyle = '#fff';
  g.fill();
  g.restore();

  const size = Math.min(38, Math.round(rowH * 0.58));
  let y = top + 14;
  rows.forEach((p, i) => {
    const mine = p.id === meId;
    const mid = y + rowH / 2;
    if (mine) {
      roundedRect(g, x + 12, y + 3, w - 24, rowH - 6, rowH / 2 - 3);
      g.fillStyle = '#fef3c7';
      g.fill();
    } else if (i > 0) {
      g.fillStyle = LINE;
      g.fillRect(x + 40, y, w - 80, 2);
    }
    const base = mid + size * 0.36;
    write(g, `${p.place}`, x + 64, base, { weight: 800, size, color: mine ? RED : LIGHT, align: 'center' });
    write(g, p.name, x + 118, base, { weight: 700, size, color: TEXT, max: 520 });
    if (mine) {
      g.font = font(700, size);
      fit(g, p.name, 700, size, 520);
      const nameW = g.measureText(p.name).width;
      badge(g, 'YOU', x + 118 + nameW + 50, mid, { fill: RED, color: '#fff', size: Math.round(size * 0.6) });
    }
    write(g, `${p.total.toLocaleString('en-US')} pts`, x + w - 44, base, { weight: 800, size, color: mine ? RED : TEXT, align: 'right' });
    y += rowH;
  });
  return top + h;
}

function drawStandings(g, card, copy, art, label, logo) {
  const ranked = card.ranked || [];
  const meId = card.me?.id;
  const rest = ranked.slice(3);
  const footerY = H - 56;

  // The list takes what it needs from the bottom; the podium gets the rest.
  const rowH = rest.length > 4 ? 52 : 68;
  const listH = rest.length ? rest.length * rowH + 28 : 0;
  const listTop = footerY - 56 - listH;
  const baseline = rest.length ? listTop - 30 : footerY - 76;

  // Third place's step must still hold its label, name and points.
  const room = baseline - 300;
  const tallest = Math.round(Math.max(290, Math.min(330, room * 0.55)));
  const mascot = Math.round(Math.max(110, Math.min(240, room - tallest - 60)));
  const shift = Math.max(0, (room - mascot - 60 - tallest) / 2);

  // Podium: 2nd, 1st, 3rd, centred on whichever steps are filled.
  const gap = 14;
  const w = (W - PAD * 2 - gap * 2) / 3;
  const places = [2, 1, 3].filter((place) => ranked[place - 1]);
  const startX = (W - (places.length * w + (places.length - 1) * gap)) / 2;
  const winnerX = startX + places.indexOf(1) * (w + gap) + w / 2;

  backdrop(g, winnerX, baseline - tallest - mascot * 0.5, seeded(ranked.map((p) => p.name).join('|')));

  // Brand, then the result.
  g.textBaseline = 'alphabetic';
  const brand = 'NameIt!';
  g.font = font(800, 44);
  const brandW = (logo ? 72 : 0) + g.measureText(brand).width;
  let bx = (W - brandW) / 2;
  if (logo) {
    g.save();
    roundedRect(g, bx, 50, 58, 58, 15);
    g.clip();
    g.drawImage(logo, bx, 50, 58, 58);
    g.restore();
    bx += 72;
  }
  write(g, brand, bx, 94, { weight: 800, size: 44, color: '#fff' });

  const winner = ranked[0];
  const second = ranked[1];
  const tie = second && second.total === winner?.total;
  const title = !winner ? copy.headline : tie ? `${winner.name} & ${second.name} tie!` : `${winner.name} wins!`;
  const ty = 236 + shift;
  g.save();
  if (typeof g.letterSpacing === 'string') g.letterSpacing = '6px';
  write(g, 'FINAL STANDINGS', W / 2, ty - 70, { weight: 800, size: 28, color: 'rgba(255, 255, 255, 0.8)', align: 'center' });
  g.restore();
  g.save();
  g.shadowColor = 'rgba(0, 0, 0, 0.25)';
  g.shadowBlur = 16;
  g.shadowOffsetY = 6;
  write(g, title, W / 2, ty + 20, { weight: 800, size: 96, color: '#fff', align: 'center', max: W - PAD * 2 });
  g.restore();

  places.forEach((place, i) => {
    const p = ranked[place - 1];
    const look = STEPS[place];
    const h = Math.round(tallest * look.ratio);
    step(g, p, look, startX + i * (w + gap), w, h, baseline, Math.round(mascot * (place === 1 ? 1 : 0.86)), art[p.id], p.id === meId);
  });

  if (rest.length) standingsList(g, rest, meId, listTop, rowH);

  const url = SITE_URL.replace(/^https?:\/\//, '');
  g.font = font(600, 36);
  const lead = 'Play your own at ';
  const leadW = g.measureText(lead).width;
  g.font = font(800, 36);
  const total = leadW + g.measureText(url).width;
  write(g, lead, (W - total) / 2, footerY, { weight: 600, size: 36, color: '#fff' });
  write(g, url, (W - total) / 2 + leadW, footerY, { weight: 800, size: 36, color: YELLOW });
}

function drawSolo(g, card, copy, art) {
  panel(g, null);
  headline(g, copy.headline);
  write(g, copy.sub, INNER, PANEL_TOP + 196, { weight: 700, size: 32, color: MUTED });
  const colW = (W - INNER * 2) / 2;
  const sides = [
    { key: 'me', name: card.me, score: card.userScore, lost: card.result === 'loss', color: RED },
    { key: 'bot', name: card.bot, score: card.botScore, lost: card.result === 'win', color: TEXT },
  ];
  sides.forEach((s, i) => {
    const cx = INNER + colW * i + colW / 2;
    drawMascot(g, art[s.key], cx - 100, PANEL_TOP + 226, 200, 224);
    write(g, s.name, cx, PANEL_TOP + 494, { weight: 700, size: 42, align: 'center', max: colW - 30 });
    write(g, String(s.score), cx, PANEL_TOP + 650, { weight: 800, size: 170, color: s.lost ? LIGHT : s.color, align: 'center' });
  });
  write(g, 'vs', W / 2, PANEL_TOP + 612, { weight: 600, size: 48, color: LIGHT, align: 'center' });
  if (card.best) {
    const w = chip(g, 0, 0, card.best.answer, card.best.category, true);
    write(g, 'Best answer', W / 2, PANEL_TOP + 736, { weight: 700, size: 32, color: MUTED, align: 'center' });
    chip(g, (W - w) / 2, PANEL_TOP + 760, card.best.answer, card.best.category);
  }
}

// art maps mascot keys (player ids, or "me" / "bot" for solo) to loaded images; art.logo is the N icon.
export function drawResultCard({ type, card, copy, kind, pick = 0, art = {}, label }) {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (type === 'solo') {
    header(g, label || 'Solo', art.logo);
    drawSolo(g, card, copy, art);
    footer(g, gameFacts(card), card.letters, '/solo');
    return c;
  }
  if (kind === 'standings') {
    drawStandings(g, card, copy, art, label, art.logo);
    return c;
  }
  header(g, label || 'Room results', art.logo);
  if (kind === 'won') drawWon(g, card, copy, art);
  else if (kind === 'votedOut') drawVotedOut(g, card, copy, art, pick);
  else drawPodium(g, card, copy, art);
  footer(g, gameFacts(card), card.letters);
  return c;
}

export async function cardToFile(canvas, name = 'nameit-results.png') {
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
  return blob && { blob, file: new File([blob], name, { type: 'image/png' }), url: URL.createObjectURL(blob) };
}
